import {
  ElementRef,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { dayjs } from 'utils/providers/Timezone';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { cn } from 'utils/helpers';
import { useQueryParams } from 'hooks';
import {
  useGetAccountUsers,
  useGetCompanies,
  useGetLeads,
} from 'modules/account/contact/hooks';
import { accountUserFilterMapper } from 'modules/account/contact/transformers/dataMapper';
import {
  DealsList,
  DealsKanbanBoard,
  CreateDealModal,
  DealsMoveModal,
  EditDealsModal,
} from 'modules/account/sales/components';
import {
  useDeleteDeals,
  useGetPipelines,
  useGetStages,
} from 'modules/account/sales/hooks';
import { Button, DeleteItemModal, FloatingBar } from 'ui/components/common';
import { Filter, Searchbox } from 'ui/components/form';
import FILTER_TYPES from 'ui/components/form/FilterForm/filterTypes';
import { PipelineIcon } from 'ui/components/icon';
import { NavAction, PageHeader } from 'ui/components/nav';
import { sanitizeParams } from 'utils/helpers';
import basicArrayMapper from 'utils/helpers/mappers/basicArrayMapper';
import { debounce, isString, size } from 'lodash';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { Composer } from 'ui/partials';
import useModal from 'ui/partials/Modal/useModal';
import c, {
  COMPOSER_TYPES,
  DATE_OPTIONS,
  POSITION,
  SIZE,
} from 'utils/constants';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { DATA_RECORD_SOURCE } from 'utils/types';
import { ExportDataConfiguratorModal } from 'modules/account/contact/components';

const FILTER_INIT = {
  accountUserId: [],
  leadId: [],
  companyId: '',
};

const FILTER_OPTIONS = {
  createdAt: FILTER_TYPES.date,
  accountUserId: FILTER_TYPES.accountUsers,
  leadId: FILTER_TYPES.accountUsers,
};

type CONTEXT = {
  pipelineId: string;
  query: Record<string, any>;
  selectedDealId?: string;
  setSelectedDealId?: React.Dispatch<React.SetStateAction<string | null>>;
  selectedDeals?: string[];
  setSelectedDeals?: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedNoteId?: React.Dispatch<React.SetStateAction<string | null>>;
  selectedNodeId?: string | null;
};

export const DealsContext = createContext<CONTEXT>({
  pipelineId: '',
  query: {},
  selectedDealId: '',
});

// eslint-disable-next-line react-refresh/only-export-components
export const useDealsContext = () => useContext(DealsContext);

const { ACCESS_LEVEL, PERMISSION_KEYS, ACTIONS } = c;

const Deals = () => {
  const {
    state: { relativePath },
  } = useAppWrapperContext();
  const navigate = useNavigate();
  const [query, setQuery] = useQueryParams();
  const { showModal, open, hideModal } = useModal();
  const canAddDeal = useCanRolePermissions(
    ACTIONS.CREATE,
    PERMISSION_KEYS.DEALS,
  );
  const canDeleteDeal = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.DEALS,
  );
  const canEditDeal = useCanRolePermissions(
    ACTIONS.UPDATE,
    PERMISSION_KEYS.DEALS,
  );
  const canViewLeadsAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.LEADS,
  );
  const canViewLeadsOnly = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_USER,
    PERMISSION_KEYS.LEADS,
  );
  const canViewCompaniesAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.COMPANIES,
  );
  const canViewCompaniesOnly = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_USER,
    PERMISSION_KEYS.COMPANIES,
  );

  const { data: pipelines } = useGetPipelines();
  const searchboxRef = useRef<ElementRef<'input'>>(null);

  const [form, setForm] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isBoardView, setIsBoardView] = useState<boolean>(true);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(
    null,
  );
  const [selectedDeals, setSelectedDeals] = useState<string[]>([]);
  const [createdStartDate, setCreatedStartDate] = useState<Date | undefined>(
    undefined,
  );
  const [createdEndDate, setCreatedEndDate] = useState<Date | undefined>(
    undefined,
  );
  const [dealOwnerSearch, setDealOwnerSearch] = useState('');
  const [contactsSearch, setContactsSearch] = useState('');
  const [companiesSearch, setCompaniesSearch] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_filterOptions, setFilterOptions] =
    useState<Record<string, any>>(FILTER_OPTIONS);

  const { data: accountUsersData } = useGetAccountUsers({
    keyword: dealOwnerSearch,
  });
  const { data: leadsData } = useGetLeads({
    keyword: contactsSearch,
    enabled: canViewLeadsAll || canViewLeadsOnly,
  });
  const { data: companiesData } = useGetCompanies({
    keyword: companiesSearch,
    enabled: canViewCompaniesAll || canViewCompaniesOnly,
  });

  const { data: stages } = useGetStages(selectedPipelineId);
  const { mutate: deleteDeals, isLoading: deleteLoading } = useDeleteDeals();

  const accountUsersOptions = useCallback(
    (values: string[]) =>
      accountUsersData?.data?.map((users) => ({
        value: users._id,
        label: users.fullName,
        description: users.emailAddress,
        isSelected: values?.includes(users._id),
        withCheckbox: true,
      })) ?? [],
    [accountUsersData?.data],
  );

  const leadsOptions = useCallback(
    (values: string[]) =>
      leadsData?.data?.map((lead) => ({
        value: lead.id,
        label: lead.fullName,
        description: lead.emailAddress,
        isSelected: values?.includes(lead.id),
        withCheckbox: true,
      })) ?? [],
    [leadsData?.data],
  );

  const companyOptions = useCallback(
    (values: string[]) =>
      companiesData?.data.map((company) => ({
        value: company.id,
        label: company.name,
        description: company.emailAddress,
        isSelected: values?.includes(company.id),
        withCheckbox: true,
      })) ?? [],
    [companiesData?.data],
  );

  const handleSearch = debounce((val: string) => setDealOwnerSearch(val), 200);

  useEffect(() => {
    if (!selectedPipelineId && pipelines && pipelines?.length) {
      const primaryPipeline = pipelines.find(
        (pipeline) => pipeline.isPrimary === true,
      );

      if (primaryPipeline) {
        setSelectedPipelineId(primaryPipeline._id);
      } else {
        setSelectedPipelineId(pipelines[0]._id);
      }
    }
  }, [pipelines, selectedPipelineId]);

  useEffect(() => {
    setForm(null);
  }, [setForm]);

  const createdAt = useMemo(
    () =>
      createdStartDate && createdEndDate
        ? {
            $gte: dayjs(createdStartDate).toDate(),
            $lt: dayjs(createdEndDate).add(1, 'days').toDate(),
          }
        : {},
    [createdStartDate, createdEndDate],
  );

  useEffect(() => {
    if (form)
      setTimeout(
        () =>
          showModal({
            title: 'Create Deal',
            disableOutsideClick: true,
            closeCallback: () => setForm(null),
            content: (
              <CreateDealModal
                selectedPipelineId={selectedPipelineId!}
                form={form}
                setForm={setForm}
              />
            ),
          }),
        300,
      );
  }, [form, open, showModal, selectedPipelineId]);

  useEffect(() => {
    if (!pipelines?.length) return;

    let accountUsersMap: any[] = [];
    let leadsMap: { itemLabel: string; value: string; description: string }[] =
      [];
    let companyMap: {
      itemLabel: string;
      value: string;
      description: string;
    }[] = [];

    if (accountUsersData?.data?.length) {
      //TODO: Type properly
      accountUsersMap = basicArrayMapper(accountUsersData.data as any)(
        accountUserFilterMapper,
      );
    }

    if (leadsData) {
      leadsMap = leadsData.data.map((lead) => ({
        itemLabel: lead.fullName,
        value: lead._id,
        description: lead.emailAddress,
      }));
    }

    if (companiesData) {
      companyMap = companiesData.data.map((company) => ({
        itemLabel: company.name,
        value: company.id,
        description: company.emailAddress as string,
      }));
    }

    setFilterOptions((prevVal) => ({
      ...prevVal,
      accountUserId: accountUsersMap,
      leadId: leadsMap,
      companyId: companyMap,
    }));
  }, [pipelines, accountUsersData, leadsData, companiesData]);

  const selectedPipeline = useMemo(() => {
    if (pipelines && selectedPipelineId) {
      return pipelines?.find((pipeline) => pipeline._id === selectedPipelineId);
    }

    return null;
  }, [pipelines, selectedPipelineId]);

  const navOptions = useMemo(() => {
    if (pipelines && pipelines.length > 1) {
      return pipelines.map((pipeline) => ({
        text: pipeline.name,
        key: pipeline._id,
        onClick: () => setSelectedPipelineId(pipeline._id),
      }));
    }

    return [];
  }, [pipelines]);

  useEffect(() => {
    if (size(createdAt) > 0) {
      setQuery((prev) => ({
        ...prev,
        createdAt: JSON.stringify(createdAt),
      }));
    }
  }, [createdAt, setQuery]);

  // TODO: Type this Properly
  const handleFilterOnChange = (filters: Record<string, any>) => {
    setQuery(
      (prev) =>
        ({
          ...prev,
          ...(size(createdAt) > 0 && {
            createdAt: JSON.stringify(createdAt) as string,
          }),
          ...sanitizeParams(filters),
        }) as Record<string, any>,
    );
  };

  const handleShowCreateModal = () => {
    if (!pipelines?.length) {
      return;
    }

    showModal({
      title: 'Create Deal',
      disableOutsideClick: true,
      closeCallback: () => setForm(null),
      content: (
        <CreateDealModal
          selectedPipelineId={selectedPipelineId!}
          form={form}
          setForm={setForm}
        />
      ),
    });
  };

  const onChangeCreateDate = (dates: (Date | undefined)[]) => {
    const [start, end] = dates;
    setCreatedStartDate(start);
    setCreatedEndDate(end);
  };

  // TODO: Type Properly
  const value: any = useMemo(
    () => ({
      query: { ...FILTER_INIT, ...query },
      pipelineId: selectedPipelineId,
      setSelectedDealId,
      setSelectedDeals,
      selectedDeals,
      selectedDealId,
      form,
      setForm,
    }),
    [query, selectedPipelineId, selectedDeals, selectedDealId, form],
  );

  const handleClickSettings = () => {
    navigate(`${relativePath}sales/settings`);
  };

  const handleToggleBoardView = (boardView: boolean) => {
    if (!pipelines?.length) {
      return;
    }

    setIsBoardView(boardView);
  };

  const clearLeadOwners = (handleChange: (key: string, value: any) => void) => {
    setDealOwnerSearch('');
    handleChange('accountUserId', []);
  };

  const clearCreatedAt = (handleChange: (key: string, value: any) => void) => {
    handleChange('createdAt', undefined);
    setCreatedStartDate(undefined);
    setCreatedEndDate(undefined);
  };

  const clearCompany = (handleChange: (key: string, value: any) => void) => {
    setCompaniesSearch('');
    handleChange('companyId', []);
  };

  const clearLeadId = (handleChange: (key: string, value: any) => void) => {
    setContactsSearch('');
    handleChange('leadId', []);
  };

  const clearAllFilters = (handleChange: (key: string, value: any) => void) => {
    clearLeadOwners(handleChange);
    clearCreatedAt(handleChange);
    clearCompany(handleChange);
    clearLeadId(handleChange);
    handleChange('keyword', '');
    searchboxRef.current!.value = '';
  };

  const handleShowMoveDeals = () => {
    showModal({
      size: SIZE.Small,
      title: 'Move Deals',
      content: (
        <DealsMoveModal
          setSelectedDeals={setSelectedDeals}
          dealIds={selectedDeals}
        />
      ),
    });
  };

  const handleShowDeleteDeals = () => {
    showModal({
      title: 'Delete Deals',
      content: (
        <DeleteItemModal
          onClickDelete={() =>
            deleteDeals(selectedDeals, {
              onSuccess: () => {
                hideModal();
                setSelectedDeals([]);
              },
            })
          }
          text="Deals"
          isMany
          isLoading={deleteLoading}
          validation
        />
      ),
    });
  };

  const handleShowEditDeals = () => {
    showModal({
      title: 'Assign Tags',
      content: <EditDealsModal dealIds={selectedDeals} />,
    });
  };

  const handleExport = () => {
    showModal({
      title: 'Export',
      resetPadding: true,
      content: (
        <ExportDataConfiguratorModal
          source={DATA_RECORD_SOURCE.DEAL}
          onClose={hideModal}
        />
      ),
      position: POSITION.Top,
      size: 'xl',
    });
  };

  const handleImport = () => {
    navigate('/data/new?source=deals');
  };

  return (
    <DealsContext.Provider value={value}>
      <p className="mb-[22px] text-[16px] font-semibold">Deals</p>
      <PageHeader
        filters={query}
        onFilterChange={handleFilterOnChange}
        onClickCreate={canAddDeal ? handleShowCreateModal : undefined}
        onClickExport={handleExport}
        onClickImport={canAddDeal ? handleImport : undefined}
        isBoardView={isBoardView}
        disableCreate={!stages?.length}
        onToggleView={handleToggleBoardView}
        onClickSettings={handleClickSettings}
        createDataTut={TOUR_SELECTOR.TOUR.SALES.DEAL.FORM.CREATE_BUTTON}
        settingsDataTut={TOUR_SELECTOR.TOUR.SALES.DEAL.SETTINGS_BUTTON}
        createTooltip={
          stages?.length ? undefined : 'Please add a stage to create a deal.'
        }
        type="deal"
      >
        {({ values, handleChange }) => (
          <>
            <div
              className={cn(
                'flex items-center justify-between',
                'mb-4 pb-[26px]',
                'border-b border-[#E6F0FF]',
              )}
            >
              {!!pipelines?.length && selectedPipeline ? (
                <NavAction
                  optionsClassName="w-[240px] left-0 z-30"
                  options={navOptions}
                  label={
                    <p
                      className={cn(
                        'flex items-center gap-x-3',
                        'text-[16px] font-semibold',
                      )}
                    >
                      {selectedPipeline.name}
                      {navOptions && <HiOutlineChevronDown />}
                    </p>
                  }
                />
              ) : (
                <p className="text-[16px] font-semibold">
                  NO PIPELINE CREATED YET
                </p>
              )}
            </div>
            <div className={cn('rounded-tr-[6px]', 'rounded-b-[6px]')}>
              <div className={cn('flex gap-x-3')}>
                <Searchbox
                  ref={searchboxRef}
                  className="sm w-[220px]"
                  name="keyword"
                  placeholder="Search deal"
                  onChange={debounce(
                    (event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange('keyword', event.target.value);
                    },
                    300,
                  )}
                />
                <div
                  data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DEALS_FILTER}
                  className="flex gap-x-3"
                >
                  <Filter
                    isMulti
                    placeholder="Deal Owner"
                    defaultValue={values.accountUserId}
                    options={accountUsersOptions(values.accountUserId)}
                    withSearch
                    onSearch={(e) => handleSearch(e)}
                    onClear={() => {
                      setDealOwnerSearch('');
                      handleChange('accountUserId', []);
                    }}
                    onChange={(opt) => {
                      const selectedUsers = values.accountUserId;
                      if (!selectedUsers) {
                        handleChange('accountUserId', [opt.value]);
                        return;
                      }

                      let accountUsers = isString(selectedUsers)
                        ? [selectedUsers]
                        : [...selectedUsers];

                      if (accountUsers.includes(opt.value)) {
                        accountUsers = [...selectedUsers].filter(
                          (id) => id !== opt.value,
                        );
                      } else {
                        accountUsers.push(opt.value);
                      }

                      handleChange('accountUserId', accountUsers);
                    }}
                  />
                  <Filter
                    placeholder="Date Added"
                    options={DATE_OPTIONS}
                    isDateField
                    defaultValue={values.createdAt}
                    startDate={createdStartDate}
                    endDate={createdEndDate}
                    onChangeDate={(dates) => onChangeCreateDate(dates)}
                    onClear={() => {
                      handleChange('createdAt', undefined);
                      setCreatedStartDate(undefined);
                      setCreatedEndDate(undefined);
                    }}
                    onChange={(opt) => handleChange('createdAt', opt.value)}
                  />
                  {!!leadsData?.data.length && (
                    <Filter
                      isMulti
                      defaultValue={values.leadId}
                      selectedClassName="w-max"
                      placeholder="Associated Contact"
                      options={leadsOptions(values.leadId)}
                      withSearch
                      onSearch={(e) => handleSearch(e)}
                      onClear={() => clearLeadId(handleChange)}
                      onChange={(opt) => {
                        const selectedLeads = values.leadId;
                        if (!selectedLeads) {
                          handleChange('leadId', [opt.value]);
                          return;
                        }

                        let leads = isString(selectedLeads)
                          ? [selectedLeads]
                          : [...selectedLeads];

                        if (leads.includes(opt.value)) {
                          leads = [...selectedLeads].filter(
                            (id) => id !== opt.value,
                          );
                        } else {
                          leads.push(opt.value);
                        }

                        handleChange('leadId', leads);
                      }}
                    />
                  )}
                  {!!companiesData?.data.length && (
                    <Filter
                      selectedClassName="w-max"
                      placeholder="Associated Company"
                      defaultValue={values.companyId}
                      options={companyOptions(values.companyId)}
                      withSearch
                      onSearch={(e) => handleSearch(e)}
                      onClear={() => clearCompany(handleChange)}
                      onChange={(opt) => handleChange('companyId', opt.value)}
                    />
                  )}
                </div>
                {
                  <Button
                    intent={'text'}
                    label="Clear Filters"
                    onClick={() => clearAllFilters(handleChange)}
                  />
                }
              </div>
              {!pipelines?.length && (
                <div className="h-[calc(100vh-333px)] space-y-[22px] bg-white pt-24 text-center">
                  <div className="mx-auto h-[104px] w-fit rounded-full bg-[#F2FAFF] p-8">
                    <PipelineIcon className="h-10 w-10 text-content" />
                  </div>
                  <div>
                    <p className="text-[22px] font-semibold">Empty Pipeline</p>
                    <p className="mt-1 text-sm">
                      There seems to be no pipeline available to show.
                    </p>
                  </div>
                  <Button
                    size="md"
                    intent={'primary'}
                    label="GO TO PIPELINE SETTINGS"
                    className="mx-auto"
                    onClick={handleClickSettings}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </PageHeader>
      {pipelines?.length && isBoardView && <DealsKanbanBoard />}
      {pipelines?.length && !isBoardView && <DealsList />}
      <Composer
        type={COMPOSER_TYPES.deal}
        onSubmit={async ({ body, attachments }) => {
          console.log({ body, attachments });
        }}
        closeComposer={() => setSelectedDealId(null)}
        isOpen={!!selectedDealId}
        isLoading={false}
      />
      <FloatingBar
        ids={selectedDeals}
        onDelete={canDeleteDeal && handleShowDeleteDeals}
        onUpdate={canEditDeal && handleShowEditDeals}
        onMove={canEditDeal && handleShowMoveDeals}
      />
    </DealsContext.Provider>
  );
};

export default Deals;
